import { z } from "zod";

import {
  deriveFieldId,
  MAX_FIELD_ID_LENGTH,
} from "routes/custom_tables/helpers";

export { deriveFieldId };

// A field_id must start with a letter and use only lowercase letters, numbers,
// and underscores — matching the backend identifier rules.
export const FIELD_ID_PATTERN = /^[a-z][a-z0-9_]*$/;

// The custom-table column-type enum (mirrors CustomTableColumnType on the
// backend; labels live in typeLabels.js).
export const COLUMN_TYPES = [
  "short_text",
  "text",
  "number",
  "date",
  "boolean",
  "single_select",
  "multi_select",
  "list",
  "relationship",
  "email",
  "url",
  "phone",
  "percentage",
  "currency",
];

const SELECT_TYPES = new Set(["single_select", "multi_select"]);

/**
 * Zod schema for the field-definition form (FieldDialog).
 *
 * `mode` and `existingFieldIds` are closed over so the field_id rules
 * (pattern, length, uniqueness) only apply in "add" mode — in "edit" mode the
 * field_id and type are locked and not part of the editable payload.
 */
export const buildColumnFormSchema = ({
  existingFieldIds = [],
  mode = "add",
} = {}) =>
  z
    .object({
      display_name: z.string().trim().min(1, "Display name is required"),
      field_id: z.string(),
      type: z.enum(COLUMN_TYPES),
      is_required: z.boolean(),
      is_unique: z.boolean(),
      choices: z.array(
        z.object({ value: z.string().min(1), color: z.string() })
      ),
      target_type: z.string().nullable(),
      label_field_id: z.string().nullable(),
    })
    .superRefine((val, ctx) => {
      if (mode === "add") {
        const id = val.field_id;
        if (!FIELD_ID_PATTERN.test(id)) {
          ctx.addIssue({
            path: ["field_id"],
            code: z.ZodIssueCode.custom,
            message:
              "Field ID must start with a letter and use only lowercase letters, numbers, and underscores",
          });
        } else if (id.length > MAX_FIELD_ID_LENGTH) {
          ctx.addIssue({
            path: ["field_id"],
            code: z.ZodIssueCode.custom,
            message: `Field ID must be ${MAX_FIELD_ID_LENGTH} characters or fewer`,
          });
        } else if (existingFieldIds.includes(id)) {
          ctx.addIssue({
            path: ["field_id"],
            code: z.ZodIssueCode.custom,
            message: `A field with the ID "${id}" already exists`,
          });
        }
      }
      if (SELECT_TYPES.has(val.type) && val.choices.length === 0) {
        ctx.addIssue({
          path: ["choices"],
          code: z.ZodIssueCode.custom,
          message: "Add at least one choice",
        });
      }
      if (val.type === "relationship" && !val.target_type) {
        ctx.addIssue({
          path: ["target_type"],
          code: z.ZodIssueCode.custom,
          message: "Pick a target",
        });
      }
    });
